import { KAPLAYCtx } from "kaplay";
import { GAME, PHYSICS, PLAYER, RENDERING } from "./constants";
import { getCurrentSkyColors, interpolateColor } from "./day-night-cycle";
import { getTerrainY } from "./terrain";
import type {
  Camera,
  DayNightCycle,
  Player,
  TerrainPoint,
  TrailPoint,
} from "./types";

export function renderSky(
  k: KAPLAYCtx,
  camera: Camera,
  dayNight: DayNightCycle,
): void {
  const viewportWidth = k.width() / camera.scale;
  const viewportHeight = k.height() / camera.scale;
  const skyWidth = viewportWidth + 2000;
  const skyHeight = viewportHeight + 2000;
  const currentCameraPos = k.getCamPos();
  const skyColors = getCurrentSkyColors(dayNight.timeOfDay);

  const numBands = 100;
  const bandHeight = skyHeight / numBands;

  for (let i = 0; i < numBands; i++) {
    const t = i / (numBands - 1);
    let color: [number, number, number];

    if (t < 0.5) {
      const localT = t * 2;
      color = interpolateColor(skyColors.top, skyColors.middle, localT);
    } else {
      const localT = (t - 0.5) * 2;
      color = interpolateColor(skyColors.middle, skyColors.bottom, localT);
    }

    k.drawRect({
      width: skyWidth,
      height: bandHeight + 1,
      pos: k.vec2(
        currentCameraPos.x - skyWidth / 2,
        currentCameraPos.y - skyHeight / 2 + i * bandHeight,
      ),
      color: k.rgb(...color),
    });
  }
}

export function getVisibleTerrain(
  terrain: TerrainPoint[],
  viewLeft: number,
  viewRight: number,
): TerrainPoint[] {
  let startIndex = 0;
  let endIndex = terrain.length - 1;

  for (const [i, element] of terrain.entries()) {
    if (element.x >= viewLeft) {
      startIndex = Math.max(0, i - 1);
      break;
    }
  }

  for (let i = startIndex; i < terrain.length; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (terrain[i].x > viewRight) {
      endIndex = Math.min(terrain.length - 1, i + 1);
      break;
    }
  }

  return terrain.slice(startIndex, endIndex + 1);
}

export function extendTerrainBoundaries(
  visibleTerrain: TerrainPoint[],
  viewLeft: number,
  viewRight: number,
  fullTerrain: TerrainPoint[],
): TerrainPoint[] {
  const extended = [...visibleTerrain];

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (extended[0].x > viewLeft) {
    extended.unshift({ x: viewLeft, y: getTerrainY(viewLeft, fullTerrain) });
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (extended[extended.length - 1].x < viewRight) {
    extended.push({ x: viewRight, y: getTerrainY(viewRight, fullTerrain) });
  }

  return extended;
}

export function renderTerrainPolygon(
  k: KAPLAYCtx,
  terrain: TerrainPoint[],
  cameraPos: { x: number; y: number },
  viewportHeight: number,
  color?: readonly [number, number, number],
  opacity?: number,
): void {
  const bottomY = cameraPos.y + viewportHeight / 2 + 200;

  const terrainColor = color || RENDERING.TERRAIN_COLOR;
  const terrainOpacity = opacity || 1.0;

  for (let i = 0; i < terrain.length - 1; i++) {
    const p1 = terrain[i];
    const p2 = terrain[i + 1];

    const segmentPoly = [
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p1.x, y: bottomY },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p1.x, y: p1.y },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p2.x, y: p2.y },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p2.x, y: bottomY },
    ];

    k.drawPolygon({
      pts: segmentPoly.map((p) => k.vec2(p.x, p.y)),
      color: k.rgb(...terrainColor),
      opacity: terrainOpacity,
    });
  }
}

export function renderTerrainOutline(
  k: KAPLAYCtx,
  terrain: TerrainPoint[],
  cameraScale: number,
): void {
  for (let i = 0; i < terrain.length - 1; i++) {
    k.drawLine({
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      p1: k.vec2(terrain[i].x, terrain[i].y),
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      p2: k.vec2(terrain[i + 1].x, terrain[i + 1].y),
      width: RENDERING.TERRAIN_OUTLINE_WIDTH / cameraScale,
      color: k.rgb(...RENDERING.TERRAIN_OUTLINE_COLOR),
    });
  }
}

export function renderTerrain(
  k: KAPLAYCtx,
  terrain: TerrainPoint[],
  camera: Camera,
): void {
  const currentCameraPos = k.getCamPos();
  const viewportWidth = k.width() / camera.scale;
  const viewportHeight = k.height() / camera.scale;
  const viewLeft = currentCameraPos.x - viewportWidth / 2 - 2000;
  const viewRight = currentCameraPos.x + viewportWidth / 2 + 2000;

  const visibleTerrain = getVisibleTerrain(terrain, viewLeft, viewRight);

  if (visibleTerrain.length <= 1) {
    return;
  }

  const extendedTerrain = extendTerrainBoundaries(
    visibleTerrain,
    viewLeft,
    viewRight,
    terrain,
  );

  renderTerrainPolygon(k, extendedTerrain, currentCameraPos, viewportHeight);
  renderTerrainOutline(k, extendedTerrain, camera.scale);
}

export function renderFinishLineMarker(
  k: KAPLAYCtx,
  camera: Camera,
  terrain: TerrainPoint[],
): void {
  const finishX = GAME.START_X + GAME.TARGET_DISTANCE;
  const currentCameraPos = k.getCamPos();
  const viewportWidth = k.width() / camera.scale;

  if (
    finishX < currentCameraPos.x - viewportWidth ||
    finishX > currentCameraPos.x + viewportWidth * 2
  ) {
    return;
  }

  const terrainY = getTerrainY(finishX, terrain);
  const flagHeight = 200 / camera.scale;
  const flagWidth = 80 / camera.scale;
  const poleHeight = 250 / camera.scale;

  k.drawLine({
    p1: k.vec2(finishX, terrainY),
    p2: k.vec2(finishX, terrainY - poleHeight),
    width: 4 / camera.scale,
    color: k.rgb(139, 69, 19),
  });

  const checkerSize = 20 / camera.scale;
  const flagTop = terrainY - poleHeight;
  const flagBottom = flagTop + flagHeight;
  const flagLeft = finishX;
  const flagRight = finishX + flagWidth;

  for (let y = 0; y < Math.ceil(flagHeight / checkerSize); y++) {
    for (let x = 0; x < Math.ceil(flagWidth / checkerSize); x++) {
      const isBlack = (x + y) % 2 === 0;
      const rectX = flagLeft + x * checkerSize;
      const rectY = flagTop + y * checkerSize;
      const rectWidth = Math.min(checkerSize, flagRight - rectX);
      const rectHeight = Math.min(checkerSize, flagBottom - rectY);

      k.drawRect({
        width: rectWidth,
        height: rectHeight,
        pos: k.vec2(rectX, rectY),
        color: isBlack ? k.rgb(0, 0, 0) : k.rgb(255, 255, 255),
      });
    }
  }

  k.drawText({
    text: "FINISH",
    pos: k.vec2(
      finishX + flagWidth / 2,
      terrainY - poleHeight - 30 / camera.scale,
    ),
    color: k.rgb(255, 255, 0),
    size: 24 / camera.scale,
    anchor: "center",
    outline: { width: 2 / camera.scale, color: k.rgb(0, 0, 0) },
  });
}

export function renderTrail(k: KAPLAYCtx, trail: TrailPoint[]): void {
  const playerPixelRadius = PLAYER.RADIUS * PHYSICS.PTM_RATIO;
  const spriteSize = playerPixelRadius * 2;

  for (let i = 0; i < trail.length - 1; i++) {
    const current = trail[i];
    const next = trail[i + 1];

    k.drawLine({
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      p1: k.vec2(current.x, current.y),
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      p2: k.vec2(next.x, next.y),
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      width: (spriteSize / 2) * current.alpha,
      color: k.rgb(255, 255, 255),
    });
  }
}

export function renderPlayer(
  k: KAPLAYCtx,
  player: Player,
  sprite = "player",
  verticalOffset = 0,
  customPlayerRadius: number = PLAYER.RADIUS,
): void {
  const playerPixelRadius = customPlayerRadius * PHYSICS.PTM_RATIO;
  const spriteSize = playerPixelRadius * 2;

  const angleDegrees = (player.angle * 180) / Math.PI;

  k.drawSprite({
    sprite,
    pos: k.vec2(player.x, player.y + verticalOffset),
    width: spriteSize,
    height: spriteSize,
    angle: angleDegrees,
    anchor: "center",
  });
}
